import { useRouter } from 'next/router';
import ArticleSubscriptionsContextProvider, {
  useArticlesSubscriptionsContext,
} from '@/creditflowresearch/application/ArticlesAndStats/ArticlesSubscriptions';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import capitalize from '@framework/data-helpers/string/capitalize';
import { mapArticleTypeToTitle } from '@/creditflowresearch/ui/components/creditFlow/articles/constants';
import { getLabel, getLocationDisplayName, getProductName } from './helpers';
import SubscribeButton from '@/creditflowresearch/ui/components/creditFlow/articles/common/SubscribeButton';
import {
  useUserSubscriptionsContext,
} from '@/creditflowresearch/application/Subscriptions/UserSubscriptionsContext/UserSubscriptionsContext';
import { getRegionIdsFromMarkets } from '@/creditflowresearch/application/helpers/region';
import { useIsGuest, useIsTileSubscribeLocked } from '@/creditflowresearch/application/User/access';
import LoginOrSignUpPopover from '@/creditflowresearch/ui/common/components/LoginOrSignUpPopover';
import TileSubscribeLockedPopup from './TileSubscribeLockedPopup';

const TileSubscribeButton = ({
  themeType,
  buttonClassName,
}) => {
  const {
    productFilters,
    regionFilters,
    pageFilters: {
      productIds,
      regionIds,
      sector,
      industry,
    },
    company,
  } = usePageFiltersContext();
  const {
    isSubscribed,
    onSubscribe,
    isCorporateContext,
    isFetching,
    isCommonButtonUsed,
  } = useArticlesSubscriptionsContext();
  const { isCommonButtonSubscribed } = useUserSubscriptionsContext();
  const {
    query: {
      sector: isSectorPage,
      industry: isIndustryPage,
      companyViewId,
      markets,
    },
  } = useRouter();
  const isGuest = useIsGuest();
  const isSubscribeLocked = useIsTileSubscribeLocked();

  const marketDataName = productFilters.find(({ id }) => id === productIds[0])?.marketDataName;
  const selectedRegionsIds = markets?.length ? getRegionIdsFromMarkets(markets as string) : regionIds;
  const selectedRegions = regionFilters.filter(({ id }) => selectedRegionsIds.includes(id));

  const shortDisplayName = getLocationDisplayName(isCorporateContext, markets, selectedRegions);

  const name = getProductName({
    companyViewId,
    companyName: company?.name,
    isSectorPage,
    sectorPageTitleName: sector?.pageTitleName,
    isIndustryPage,
    industryPageTitleName: industry?.pageTitleName,
    markets,
    marketDataName,
  });

  const isButtonSubscribed = isCommonButtonUsed ? isCommonButtonSubscribed : isSubscribed;
  const isFilterSelected = productIds.length === 1;

  const label = getLabel({
    isOneFilterSelected: isFilterSelected,
    isSubscribed: isButtonSubscribed,
    shortDisplayName,
    name,
    articleType: mapArticleTypeToTitle[themeType],
    isCompanyPage: !!companyViewId,
    isCorporateContext,
  });

  const isDisabled = isButtonSubscribed
                     || (!companyViewId && !isFilterSelected)
                     || isGuest
                     // TODO: remove when the European market opening period ends
                     || isSubscribeLocked;

  return (
    <LoginOrSignUpPopover isRestricted={ isGuest } placement="bottom-end" isBlockLevelTrigger>
      <TileSubscribeLockedPopup isLocked={ isSubscribeLocked && !isGuest }>
        <SubscribeButton
          tooltip={ label }
          onClick={ onSubscribe }
          isDisabled={ isDisabled }
          isTooltipDisabled={ isGuest || isSubscribeLocked }
          isFetching={ isFetching }
          isSubscribed={ isButtonSubscribed }
          dataTest={ `subscribeButton${ capitalize(themeType) }` }
          className={ buttonClassName }
        />
      </TileSubscribeLockedPopup>
    </LoginOrSignUpPopover>
  );
};

export default ({
  themeType,
  buttonClassName,
}) => (
  <ArticleSubscriptionsContextProvider themeType={ themeType }>
    <TileSubscribeButton themeType={ themeType } buttonClassName={ buttonClassName } />
  </ArticleSubscriptionsContextProvider>
);
